import { Trans, useTranslation } from '@jq4race/i18n';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@jq4race/ui';
import { AlertTriangle } from 'lucide-react';

interface DuplicatePlateDialogProps {
  /**
   * pwa-offline-first Slice 6 course correction (coordinator finding #3) —
   * generalized from a numeric `existingTicketNumber` to a display label:
   * the duplicate can now be an OFFICIAL ticket (`#7`, from the server-side
   * search) OR a locally-queued, not-yet-synced item (`P-7F3A`, its
   * provisional code) — the local outbox check has no ticket number to
   * show yet.
   */
  existingLabel: string;
  onConfirm: () => void;
  onCancel: () => void;
}

export function DuplicatePlateDialog({
  existingLabel,
  onConfirm,
  onCancel,
}: DuplicatePlateDialogProps) {
  const { t } = useTranslation('checkin');

  return (
    <Dialog open onOpenChange={(open) => !open && onCancel()}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <div className="flex items-center gap-3">
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-destructive/15">
              <AlertTriangle className="size-5 text-destructive" strokeWidth={1.5} />
            </span>
            <DialogTitle className="text-[20px]">{t('duplicatePlate.title')}</DialogTitle>
          </div>
          <DialogDescription className="mt-1">
            <Trans
              i18nKey="checkin:duplicatePlate.description"
              values={{ label: existingLabel }}
              components={{ bold: <span className="font-semibold text-foreground" /> }}
            />
          </DialogDescription>
        </DialogHeader>

        <DialogFooter>
          <Button variant="ghost" size="lg" className="w-full sm:w-auto" onClick={onCancel}>
            {t('common:actions.cancel')}
          </Button>
          <Button variant="destructive" size="lg" className="w-full sm:w-auto" onClick={onConfirm}>
            {t('duplicatePlate.confirm')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
